// Axios interceptor to handle subscription errors globally
import { BACKEND_URL } from '../config/api';
import smartToast from './smartToast';
import { authFetchJSON } from './fetchWithTimeout';

// Event for subscription errors - components can listen to this
export const SUBSCRIPTION_ERROR_EVENT = 'subscriptionError';

// Global flag to suppress other error toasts when subscription error is active
// This prevents multiple confusing error messages when the real issue is subscription
let subscriptionErrorActive = false;
let subscriptionErrorTimestamp = 0;
const SUBSCRIPTION_ERROR_SUPPRESSION_DURATION = 10000; // 10 seconds

/**
 * Check if subscription error suppression is active
 * Other components should call this before showing error toasts
 */
export const isSubscriptionErrorActive = () => {
  if (!subscriptionErrorActive) return false;
  // Auto-expire after suppression duration
  if (Date.now() - subscriptionErrorTimestamp > SUBSCRIPTION_ERROR_SUPPRESSION_DURATION) {
    subscriptionErrorActive = false;
    return false;
  }
  return true;
};

/**
 * Set subscription error active (called when we detect subscription-related 403)
 */
const setSubscriptionErrorActive = () => {
  subscriptionErrorActive = true;
  subscriptionErrorTimestamp = Date.now();
};

export const setupSubscriptionInterceptor = (navigate) => {
  // Store original fetch
  const originalFetch = window.fetch;
  
  // Override fetch to intercept requests AND responses
  window.fetch = async (...args) => {
    // CRITICAL: Inject X-Account-Id header for team member support
    // This ensures ALL fetch calls include the account context
    // BUT: Only for our backend API, not external APIs like Firebase
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const isBackendCall = url.includes('localhost:3333') || 
                          url.startsWith('/api/') ||
                          (backendUrl && url.startsWith(backendUrl));
    const isExternalApi = url.includes('googleapis.com') || 
                          url.includes('firebaseapp.com') ||
                          url.includes('firebase.com') ||
                          url.includes('vercel.com') ||
                          url.includes('vercel-storage.com');
    
    const activeAccountId = localStorage.getItem('activeAccountId');
    if (activeAccountId && args[1] && isBackendCall && !isExternalApi) {
      const options = args[1];
      if (options.headers) {
        // Check if headers is a Headers object or plain object
        if (options.headers instanceof Headers) {
          if (!options.headers.has('X-Account-Id')) {
            options.headers.set('X-Account-Id', activeAccountId);
          }
        } else {
          // Plain object headers
          if (!options.headers['X-Account-Id'] && !options.headers['x-account-id']) {
            options.headers['X-Account-Id'] = activeAccountId;
          }
        }
      } else {
        // No headers object, create one
        options.headers = { 'X-Account-Id': activeAccountId };
      }
    } else if (activeAccountId && args.length === 1 && isBackendCall && !isExternalApi) {
      // Only URL provided, add options with header
      args[1] = { headers: { 'X-Account-Id': activeAccountId } };
    }
    
    const response = await originalFetch(...args);
    
    // Clone response to read it without consuming the stream
    const clonedResponse = response.clone();
    
    // Check if it's a 403 Forbidden
    if (response.status === 403) {
      try {
        const data = await clonedResponse.json();
        
        // Check for subscription-related errors (Free model — no grace periods)
        if (data.code === 'SUBSCRIPTION_EXPIRED' ||
            data.code === 'SUBSCRIPTION_REQUIRED' ||
            data.message?.toLowerCase().includes('subscription')) {
          setSubscriptionErrorActive();
          
          window.dispatchEvent(new CustomEvent(SUBSCRIPTION_ERROR_EVENT, {
            detail: {
              code: data.code,
              message: data.message,
            }
          }));
          
          smartToast.error(data.message || 'Please update your subscription to continue.', {
            id: 'subscription-error',
            duration: 5000,
            action: {
              label: 'View Plans',
              onClick: () => {
                if (navigate) {
                  navigate('/pricing');
                } else {
                  window.location.href = '/pricing';
                }
              }
            }
          });
          return response;
        }
      } catch {
        // If not JSON or other error, just return original response
      }
    }
    
    return response;
  };
  
  // CRITICAL: Also intercept sonner's toast.error to suppress during subscription errors
  // This catches all toast.error calls across the app without modifying each file
  interceptToastError();
};

/**
 * Intercept sonner's toast methods to apply cascading error suppression.
 * This ensures that even components using raw toast.error() from sonner
 * will benefit from our error category suppression system.
 */
const interceptToastError = () => {
  // Dynamic import to avoid issues during initial load
  Promise.all([
    import('sonner'),
    import('./smartToast')
  ]).then(([{ toast }, _smartToastModule]) => {
    const originalError = toast.error;
    const originalWarning = toast.warning;
    const originalInfo = toast.info;
    
    // Error categories for grouping
    const ERROR_CATEGORIES = {
      NETWORK: ['network', 'connection', 'offline', 'timeout', 'fetch failed', 'net::err'],
      AUTH: ['unauthorized', '401', 'session expired', 'login', 'token'],
      PERMISSION: ['forbidden', '403', 'permission', 'access denied', 'not allowed'],
      SUBSCRIPTION: ['subscription', 'trial', 'subscribe', 'plan', 'expired'],
      LOAD_DATA: ['unable to load', 'failed to load', 'failed to fetch', 'could not load', 'error loading'],
      SERVER: ['500', 'server error', 'internal error', 'something went wrong'],
    };
    
    // Track active categories
    const activeCategories = new Map();
    const SUPPRESSION_WINDOW_MS = 5000;
    
    const detectCategory = (message) => {
      if (typeof message !== 'string') return null;
      const lower = message.toLowerCase();
      for (const [cat, keywords] of Object.entries(ERROR_CATEGORIES)) {
        if (keywords.some(k => lower.includes(k))) return cat;
      }
      return null;
    };
    
    const shouldSuppress = (message, options = {}) => {
      if (options?.allowDuplicate) return false;
      
      const category = detectCategory(message);
      if (!category) return false;
      
      const active = activeCategories.get(category);
      if (active && Date.now() - active < SUPPRESSION_WINDOW_MS) {
        return true;
      }
      
      // First of this category - mark active
      activeCategories.set(category, Date.now());
      return false;
    };
    
    // Intercept error toasts
    toast.error = (message, options = {}) => {
      if (shouldSuppress(message, options)) {
        // Suppressed cascading error
        return null;
      }
      return originalError.call(toast, message, options);
    };
    
    // Intercept warning toasts
    toast.warning = (message, options = {}) => {
      if (shouldSuppress(message, options)) {
        // Suppressed cascading warning
        return null;
      }
      return originalWarning.call(toast, message, options);
    };
    
    // Intercept info toasts that are error-related
    toast.info = (message, options = {}) => {
      const isErrorRelated = typeof message === 'string' && 
        (message.toLowerCase().includes('failed') || 
         message.toLowerCase().includes('error') ||
         message.toLowerCase().includes('unable to'));
      
      if (isErrorRelated && shouldSuppress(message, options)) {
        // Suppressed cascading info
        return null;
      }
      return originalInfo.call(toast, message, options);
    };
  }).catch(() => {
    // Sonner not available, skip interception
  });
};

const normalizeTrialResponse = (data = {}) => {
  const isOnTrial = Boolean(data.isOnTrial || data.isTrialActive);
  const isFreeTier = Boolean(data.isFreeTier); // v5: true only for post-trial free users
  const isOnFreePlan = Boolean(data.isOnFreePlan); // v5: true for both free and trial (no paid subscription)
  const daysRemaining = data.daysRemaining ?? 0;
  const trialEndDate = data.trialEndDate ?? null;
  const subscriptionStatus = data.subscriptionStatus ?? 'unknown';
  const currentPlan = data.currentPlan ?? 'free'; // v5: resolved tier ("trial", "free", or paid tier)
  const normalizedStatus = String(subscriptionStatus).toLowerCase();
  const normalizedPlan = String(currentPlan).toLowerCase();
  const hasPaidPlan = normalizedPlan && !['free', 'trial', 'unknown'].includes(normalizedPlan);
  // Backend payloads are evolving; derive active state from status/plan when explicit flag is absent.
  const hasActiveSubscription = Boolean(data.hasActiveSubscription) ||
    normalizedStatus === 'active' ||
    normalizedStatus === 'trialing' ||
    hasPaidPlan;

  // v5 Pricing Model:
  // - "trial" tier (isOnTrial): 30-day full access to all features
  // - "free" tier (isFreeTier, not on trial): dashboard + ROI + upload only
  // - Paid tiers: full access per tier
  // All tiers have basic access (can log in, see dashboard)
  return {
    hasAccess: isOnTrial || isFreeTier || hasActiveSubscription,
    daysRemaining,
    trialEndDate,
    subscriptionStatus,
    currentPlan,
    isOnTrial,
    hasActiveSubscription,
    isFreeTier,
    isOnFreePlan,
    // Grace period fields — not used in current model
    inGracePeriod: false,
    graceEndsAt: null,
    graceHoursRemaining: 0,
  };
};

export const checkSubscriptionStatus = async () => {
  const _token = localStorage.getItem('accessToken');
  // Cache key for subscription status
  const CACHE_KEY = 'cachedSubscriptionStatus';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  // First check if user is a team member (non-owner) - they always have access
  // based on account owner's subscription, not their own
  try {
    const { data: teamData } = await authFetchJSON(`${BACKEND_URL}/api/team/me`);
    
    // If user is a team member with a non-owner role, grant access
    // (Account owner handles subscription, not team members)
    // Backend returns role at top level, not nested in membership object
    const role = teamData?.role || teamData?.membership?.role;
    if (role && role !== 'owner') {
      return {
        hasAccess: true,
        isTeamMember: true,
        role: role,
        daysRemaining: 999, // Team members don't care about trial
        subscriptionStatus: 'team_member',
      };
    }
  } catch {
    // Team check failed - continue with normal subscription check
    // This is fine for single-user accounts
  }
  
  try {
    const { data } = await authFetchJSON(
      `${BACKEND_URL}/api/subscriptions/trial-status`,
    );
    
    if (data) {
      const normalized = normalizeTrialResponse(data);
      const status = { ...normalized, timestamp: Date.now() };
      
      // Cache the successful response
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(status));
      } catch {
        // localStorage might be full, ignore
      }
      
      return status;
    }
  } catch (error) {
    if (error?.status !== 401) {
      console.error('Failed to check subscription status:', error);
    }
  }
  
  // On failure, try to use cached status
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const cachedStatus = JSON.parse(cached);
      const age = Date.now() - (cachedStatus.timestamp || 0);
      
      // If cache is recent (within 5 minutes), use it
      if (age < CACHE_DURATION) {
        return cachedStatus;
      }
      
      // If cache is stale but exists, still use it but warn in development
      if (age < 24 * 60 * 60 * 1000) { // Within 24 hours
        if (process.env.NODE_ENV === 'development') {
          console.warn('Using stale cached subscription status');
        }
        return cachedStatus;
      }
    }
  } catch {
    // Cache parse error, ignore
  }
  
  // No valid cache - default to restricted access to prevent abuse
  // This will cause the user to see subscription expired screen, but they can refresh
  return {
    hasAccess: false,
    daysRemaining: 0,
    trialEndDate: null,
    subscriptionStatus: 'unknown',
    error: 'Unable to verify subscription status'
  };
};
